<template>
  <AtomicUiNotification
    v-if="addToBagError"
    severity="error"
    role="alert"
    :closable="false"
    class="margin-top-s margin-bottom-xxs"
    @close="clearAddToBagError"
  >
    {{ addToBagError }}
  </AtomicUiNotification>
  <AtomicUiNotification v-if="hasError" class="margin-top-m error-container" severity="error" role="alert" :closable="false">
    {{ hasError }}
  </AtomicUiNotification>
  <div v-else class="my-list-page">
    <div class="breadcrumb-container margin-top-xs margin-bottom-xs">
      <AtomicUiBreadcrumb v-if="isBreadCrumbReady" :key="breadcrumbKey" :model="breadcrumbItems" />
    </div>
    <div class="grid-x align-justify">
      <div class="grid-x padding-bottom-xxs wishlist-header-container">
        <div :class="WISHLIST_TITLE_CLASS">
          {{ wishlistName }}
        </div>
        <div class="grid-x column-gap options-wrapper margin-top-xxs">
          <div class="body">({{ numberOfWishlistItems }})</div>
          <div v-if="(isDefaultWishlist && isWishlistOwner) || !wishlistGuid" class="large">Default</div>
        </div>
      </div>
      <div v-if="isWishlistOwner" class="grid-x align-middle">
        <ShareWishlist />
        <WishlistSettings />
        <LazyDeleteList :list-guid="wishlistGuid" />
        <WishlistAlerts v-if="brand === 'mcom'" />
      </div>
    </div>

    <WishlistFilters v-if="!isWishlistLoading && !hasError && currentWishlistItems?.length > 0" @filter-change="handleFilterChange" />

    <div class="small-margin-top-s large-margin-top-m small-margin-bottom-xl large-margin-bottom-xxl">
      <div v-if="!numberOfFilteredItems">
        <!-- eslint-disable max-len -->
        <div
          class="empty-wishlist cell small-12 medium-12 large-12 xlarge-12 border-thickness padding-top-m padding-bottom-m padding-left-l padding-right-l"
        >
          <div class="grid-x grid-margin-x align-middle">
            <div class="cell small-12 medium-12 large-12 xlarge-12 text-center">
              <AtomicUiIcon :icon="csgHeartOutlined" size="xl" class="show-for-medium" />
              <AtomicUiIcon :icon="csgHeartOutlined" size="l" class="hide-for-medium" />
              <div class="list-title heavy">This list is currently empty.</div>
            </div>
          </div>
        </div>

        <LazyProsWrapper class="large-margin-top-xxl small-margin-top-s" />
      </div>

      <div v-else class="grid-container padding-z">
        <div class="grid-x grid-margin-x grid-margin-y align-middle product-grid-x">
          <div
            v-for="(item, index) in filteredWishlistItems"
            :key="`my-list-${item.itemGuid}`"
            class="cell small-6 medium-4 large-3 small-margin-top-s large-margin-top-m"
          >
            <ProductCard
              :item="item"
              :index="index"
              :is-preparing-to-move="isPreparingToMove && itemToMove?.itemGuid === item.itemGuid"
              @move-item-click="handleMoveItemClick"
              @add-to-bag-error="handleAddToBagError"
            />
          </div>
        </div>
      </div>
    </div>
    <AtomicUiSlideout :id="slideoutId" :disable-teleport="true" :title="getTitle" position="right" @close-slideout="handleSlideoutClose">
      <LazyMoveItem
        v-if="itemToMove"
        v-model:selection="selectedListForMove"
        :item="moveItemDetails"
        :is-item-moved="isMoveItemSuccess"
        :wishlists="allWishlists"
        :move-item-error-message="moveItemErrorMessage"
        :selected-list-details="selectedListDetails"
        @navigate-to-list="navigateToList"
      />
      <template #footer>
        <div class="grid-y">
          <template v-if="!isMoveItemSuccess && showNewListInput">
            <LazyWishlistInput
              v-model:list-name="newWishlistForm.listName"
              v-model:is-public="newWishlistForm.isPublic"
              :original-list-name="''"
              class="margin-bottom-m"
              @update:has-error="newWishlistForm.hasError = $event"
            />
            <button
              class="button expanded primary margin-bottom-xs"
              :disabled="newWishlistForm.hasError || isCreatingAndMoving"
              @click="handleCreateAndMoveItem"
            >
              <AtomicUiSpinner v-if="isCreatingAndMoving" color="white" />
              <span v-else>Create</span>
            </button>
          </template>
          <template v-else-if="!isMoveItemSuccess">
            <div class="margin-bottom-xs">
              Want to save it to a
              <a href="javascript:void(0)" class="link-body" @click="showNewListCreation"> new list? </a>
            </div>
            <button
              class="button expanded primary margin-bottom-xs"
              :disabled="!selectedListForMove || selectedListForMove === wishlistGuid || isSubmittingMove"
              @click="handleMoveItem"
            >
              <AtomicUiSpinner v-if="isSubmittingMove" color="white" />
              <span v-else>Move item</span>
            </button>
          </template>

          <div v-else>
            <button class="button expanded primary margin-bottom-xs" @click="toggleMoveItemSlideout">Continue Shopping</button>
          </div>
          <button class="button secondary" @click="toggleMoveItemSlideout">Cancel</button>
        </div>
      </template>
    </AtomicUiSlideout>
  </div>
</template>

<script setup>
import { fireTag } from '@nuxt-common/util/app/utils/tagManager';
import { csgHeartOutlined } from '@csgSvgIcons/icons';
import { WISHLIST_TITLE_CLASS, brand, MOVE_ITEM_SLIDEOUT, GENERIC_ERROR_MESSAGE, headers } from 'config';
import { mapProductData } from '#utils/productCardTransform';
import { getUserGuid } from '#utils/UserDetails';
import { useSlideoutStore } from '@nuxt-common/atomic-ui/app/stores/useSlideoutStore';
import { setupBreadcrumbAnalytics, wishlistErrorAnalytics } from '~/utils/AnalyticsUtil.js';

const { isBreadCrumbReady } = defineProps({
  isBreadCrumbReady: { type: Boolean, default: false },
});
const {
  isWishlistLoading,
  numberOfWishlistItems,
  isDefaultWishlist,
  wishlistGuid,
  wishlistName,
  currentWishlistItems,
  isWishlistOwner,
  isSharedList,
} = useWishlistDetails();

const { getAllWishlists, allWishlists, numberOfWishlists, userDetails } = useWishlists();
const { createWishlist, newWishlistGuid, newWishlistName, createWishlistError, createListErrorDetails } = useCreateWishlist();

const getSelectedListStore = useGetSelectedListStore();
const moveItemStore = useMoveItemStore();
const slideoutStore = useSlideoutStore();
const filterAndSortWishlist = useWishlistFilterSort();
const router = useRouter();
const isPseudoList = computed(() => {
  const userDetailsGuid = userDetails.value?.guid;
  return userDetailsGuid === getUserGuid() && !numberOfWishlists.value;
});
const isPseudoListOrListOwner = computed(() => isWishlistOwner.value || isPseudoList.value);

const filteredWishlistItems = ref([]);
// Store the latest filter/sort criteria
const lastFilterSortSelection = ref(null);
const breadcrumbKey = ref(1);
const numberOfFilteredItems = computed(() => filteredWishlistItems.value?.length);
const items = computed(() => (filteredWishlistItems.value?.length ? filteredWishlistItems.value : currentWishlistItems.value) || []);

// Initialize filtered items with all items
watch(
  () => currentWishlistItems.value,
  (newItems) => {
    if (newItems) {
      // If we have a last filter/sort selection, re-apply it
      if (lastFilterSortSelection.value) {
        // Use shared filter/sort composable
        const { selectedFilters, sortValue } = lastFilterSortSelection.value;
        filteredWishlistItems.value = filterAndSortWishlist(newItems, selectedFilters, sortValue);
      } else {
        filteredWishlistItems.value = [...newItems];
      }
    }
  },
  { immediate: true },
);

const addToBagError = ref('');

const clearAddToBagError = () => {
  addToBagError.value = '';
};

const handleAddToBagError = (message) => {
  addToBagError.value = message;
};

watch(addToBagError, (hasAddToBagError) => {
  if (hasAddToBagError) {
    nextTick(() => {
      document.addEventListener('click', clearAddToBagError);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    });
  } else {
    document.removeEventListener('click', clearAddToBagError);
  }
});

const handleFilterChange = (filteredItems, selection) => {
  filteredWishlistItems.value = filteredItems;
  // Save the latest selection for re-filtering
  if (selection) {
    lastFilterSortSelection.value = selection;
  }
};

const hasError = computed(() => getSelectedListStore.userSelectedListsError);
const hasErrorDetails = computed(() => getSelectedListStore.userSelectedListsErrorDetails);

watch(
  hasError,
  (newError) => {
    if (newError) {
      const errorData = hasErrorDetails.value || newError;
      wishlistErrorAnalytics(errorData, fireTag);
    }
  },
  { immediate: true },
);

watch(wishlistName, (newValue) => {
  if (newValue) {
    breadcrumbKey.value += 1;
  }
});

const productColor = computed(() => items.value.map((i) => (i.upc?.color?.trim() ? i.upc.color : 'No Color')));
const productId = computed(() => items.value.map((i) => (i.upc?.product?.id != null ? String(i.upc?.product.id) : '')));
const productName = computed(() => items.value.map((i) => i.upc?.product?.name || ''));
const productOriginalPrice = computed(() =>
  items.value.map((i) => {
    const price = i.product?.price?.originalPrice || i.upc?.price?.originalPrice || '';
    return typeof price === 'number' ? price.toFixed(2) : price;
  }),
);
const productPrice = computed(() =>
  items.value.map((i) => {
    const price = i.product?.price?.retailPrice || i.upc?.price?.retailPrice || '';
    return typeof price === 'number' ? price.toFixed(2) : price;
  }),
);
const productPricingState = computed(() => items.value.map((i) => i.upc?.price?.priceTypeText || ''));
const productSize = computed(() => items.value.map((i) => i.upc?.size || ''));
const productUPC = computed(() => items.value.map((i) => (i.upc?.upcNumber != null ? String(i.upc.upcNumber) : '')));
const wishlistViewType = computed(() => (wishlistName.value ? 'owner' : 'guest'));

const moveItemErrorMessage = ref('');
const moveItemError = computed(() => moveItemStore.moveItemError);
const moveItemErrorDetails = computed(() => moveItemStore.moveItemErrorDetails);

const breadcrumbItems = computed(() => {
  const listUrl = wishlistGuid.value ? `/wishlist/mylist?listGuid=${wishlistGuid.value}` : '/wishlist/mylist';

  if (isSharedList.value) {
    return [
      {
        label: 'My Account',
        url: '/myaccount/home',
      },
      {
        label: 'Wish Lists',
        url: '/wishlist/home',
      },
    ];
  }
  const urlItems = [
    {
      label: 'My Account',
      url: '/myaccount/home',
    },
    {
      label: 'Wish Lists',
      url: isPseudoListOrListOwner.value ? '/wishlist/home' : listUrl,
    },
  ];

  if (isPseudoListOrListOwner.value) {
    urlItems.push({
      label: wishlistName.value || 'Wish List',
      url: listUrl,
    });
  }

  return urlItems;
});

const itemToMove = ref(null);
const isPreparingToMove = ref(false);
const isSubmittingMove = ref(false);
const isMoveItemSuccess = ref(false);
const selectedListForMove = ref('');
const slideoutId = 'move-item-slideout';
const showNewListInput = ref(false);
const newWishlistForm = reactive({
  listName: '',
  isPublic: [],
  hasError: true,
});
const isCreatingAndMoving = ref(false);

const showNewListCreation = () => {
  showNewListInput.value = true;
  selectedListForMove.value = '';
};

const clearMoveItemError = () => {
  if (moveItemErrorMessage.value) {
    moveItemErrorMessage.value = '';
  }
};

watch(selectedListForMove, (newValue) => {
  if (newValue && !isCreatingAndMoving.value) {
    showNewListInput.value = false;
  }
  clearMoveItemError();
});

watch([() => newWishlistForm.listName, () => [...newWishlistForm.isPublic]], clearMoveItemError);

const getTitle = computed(() => (isMoveItemSuccess.value ? MOVE_ITEM_SLIDEOUT.SUCCESS_TITLE : MOVE_ITEM_SLIDEOUT.TITLE));

const moveItemDetails = computed(() => {
  if (!itemToMove.value) return null;
  const mappedProduct = mapProductData(itemToMove.value);
  const item = itemToMove.value;
  return {
    productBrand: item?.upc?.product?.brand?.name,
    itemId: item?.itemGuid,
    registryItemId: item?.registryItemId,
    productSku: mappedProduct?.sku || item?.sku,
    displayName: mappedProduct?.name || item?.upc?.product?.name || 'Item',
    imageURL: item?.upc?.product?.imageURL,
    productColor: item?.upc?.color || 'N/A',
    productSize: item?.upc?.size || 'N/A',
    currentListGuid: wishlistGuid.value,
  };
});

const selectedListDetails = computed(() => {
  if (!selectedListForMove.value) {
    return null;
  }
  if (newWishlistGuid.value && selectedListForMove.value === newWishlistGuid.value) {
    return { name: newWishlistName.value, listGuid: newWishlistGuid.value };
  }
  if (!numberOfWishlists.value) return null;
  return allWishlists.value.find((list) => list.listGuid === selectedListForMove.value);
});

const toggleMoveItemSlideout = () => {
  slideoutStore.toggle(slideoutId);
};

const cancelNewListCreation = () => {
  showNewListInput.value = false;
  newWishlistForm.listName = '';
  newWishlistForm.isPublic = [];
  newWishlistForm.hasError = true;
};

const handleSlideoutClose = () => {
  if (isMoveItemSuccess.value) {
    getSelectedListStore.removeItemFromList(itemToMove.value.itemGuid);
  }
  itemToMove.value = null;
  isMoveItemSuccess.value = false;
  moveItemErrorMessage.value = '';
  selectedListForMove.value = '';
  cancelNewListCreation();
};

const handleMoveItemClick = async (item) => {
  isPreparingToMove.value = true;
  itemToMove.value = item;
  // Avoid blocking the main thread while preparing the heavy sideout drawer
  setTimeout(async () => {
    await getAllWishlists();
    toggleMoveItemSlideout();
    isPreparingToMove.value = false;
  }, 0);
};

const handleMoveItem = async () => {
  isSubmittingMove.value = true;
  moveItemErrorMessage.value = '';
  const payload = {
    CustomerList: {
      user: { guid: getUserGuid() },
      list: [
        {
          items: [{ itemGuid: itemToMove.value.itemGuid }],
        },
      ],
    },
  };
  await moveItemStore.moveItem({
    payload,
    newListGuid: selectedListForMove.value,
    headers,
  });
  if (moveItemError.value) {
    wishlistErrorAnalytics(moveItemErrorDetails, fireTag);
    moveItemErrorMessage.value = moveItemError.value;
  } else {
    isMoveItemSuccess.value = true;
    const item = itemToMove.value;
    fireTag('link', {
      event_name: 'wishlist move item',
      link_name: 'wishlist move item',
      product_brand: [item?.upc?.product?.brand?.name || ''],
      product_color: [item?.upc?.color || ''],
      product_id: [`${item?.upc?.product?.id}` || ''],
      product_name: [item?.upc?.product?.name || ''],
      product_original_price: [`${item?.upc?.price?.originalPrice}` || ''],
      product_price: [`${item?.upc?.price?.retailPrice}` || ''],
      product_quantity: ['1'],
      product_size: [`${item?.upc?.size}` || ''],
      product_sub_total: [`${item?.upc?.price?.retailPrice}` || ''],
      product_upc: [`${item?.upc?.upcNumber}` || ''],
    });
  }
  isSubmittingMove.value = false;
};

const handleCreateAndMoveItem = async () => {
  isCreatingAndMoving.value = true;
  moveItemErrorMessage.value = '';
  await createWishlist({
    listName: newWishlistForm.listName,
    isPublic: newWishlistForm.isPublic.length > 0,
  });
  if (createWishlistError.value || !newWishlistGuid.value) {
    moveItemErrorMessage.value = createWishlistError.value || GENERIC_ERROR_MESSAGE;
    // Fire analytics for list creation error
    const errorData = createListErrorDetails?.value || createWishlistError.value;
    wishlistErrorAnalytics(errorData, fireTag);
  } else {
    selectedListForMove.value = newWishlistGuid.value;
    await handleMoveItem();
    if (!moveItemErrorMessage.value) {
      cancelNewListCreation();
    }
  }
  isCreatingAndMoving.value = false;
};

const navigateToList = (listGuidValue) => {
  if (listGuidValue) {
    toggleMoveItemSlideout();
    router.push(`/wishlist/mylist?listGuid=${listGuidValue}`);
  }
};

onBeforeMount(async () => {
  if (!hasError.value) {
    if (brand === 'mcom') {
      fireTag('view', {
        CustomerFirstName: wishlistName.value || 'Guest',
        event_name: 'wishlist view',
        page_name: 'mylist',
        page_type: 'wishlist',
        product_color: productColor.value,
        product_id: productId.value,
        product_name: productName.value,
        product_original_price: productOriginalPrice.value,
        product_price: productPrice.value,
        product_pricing_state: productPricingState.value,
        product_size: productSize.value,
        product_upc: productUPC.value,
        userAgent: navigator.userAgent,
        wishlist_id: wishlistGuid.value,
        wishlist_view_type: wishlistViewType.value,
      });
    } else {
      fireTag('view', {
        page_name: 'list',
        page_type: 'wishlist',
        wishlist_id: wishlistGuid.value,
        wishlist_view_type: wishlistViewType.value,
      });
    }
  }
});
onMounted(() => {
  nextTick(() => {
    setupBreadcrumbAnalytics(fireTag, router);
  });
});

onUnmounted(() => {
  document.removeEventListener('click', clearAddToBagError);
});
</script>

<style lang="scss" scoped>
.empty-wishlist {
  border: $white;
  background-color: $background-color;
}
.product-grid-x {
  align-items: stretch;
}
.wishlist-header-container {
  row-gap: rem-calc(8);
  column-gap: rem-calc(8);
}
.column-gap {
  column-gap: rem-calc(8);
}
.error-container {
  margin-bottom: rem-calc(112);
  @include breakpoint(large) {
    margin-bottom: rem-calc(400);
  }
}
.options-wrapper {
  position: relative;
}
.breadcrumb-container {
  min-height: rem-calc(21);
}
</style>
